/**
 * Retailer naming from outbound product URLs (pure string helpers).
 *
 * Lifted from the SplitLinkButton component so server code and the kit cart
 * can group/label links by store without importing a client component.
 *
 * A store is named, in order: a well-known retailer (by the site's own name,
 * never a substring: "bikeaccessories.com" is not IKEA), the reviewed-merchant
 * registry, the product's brand when the link is the maker's own site, and
 * last the site's own name. Never a subdomain or a fragment of the host: the
 * 2026-09-28 QA saw "Buy Now · Shop" for shop.omnihearing.ca, "Starkeycanada"
 * and "Home-appliances" (Philips).
 */

import { hostParts } from "../server/products/buy-link-quality";
import { BRAND_TLDS, makerBrandOf } from "./buy-link-market";
import { lookupOfferMerchant } from "./offers/rank-offers";

/** Hostname of a URL, or null when it does not parse. */
export function getDomainFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    return null;
  }
}

/** Map of common retailers, keyed by the site's own name (bestbuy in www.bestbuy.ca). */
const RETAILERS: Record<string, string> = {
  amazon: "Amazon",
  walmart: "Walmart",
  bestbuy: "Best Buy",
  target: "Target",
  ebay: "eBay",
  newegg: "Newegg",
  homedepot: "Home Depot",
  lowes: "Lowe's",
  costco: "Costco",
  samsclub: "Sam's Club",
  officedepot: "Office Depot",
  staples: "Staples",
  bhphotovideo: "B&H",
  adorama: "Adorama",
  microcenter: "Micro Center",
  apple: "Apple",
  microsoft: "Microsoft",
  dell: "Dell",
  hp: "HP",
  lenovo: "Lenovo",
  sephora: "Sephora",
  ulta: "Ulta",
  macys: "Macy's",
  nordstrom: "Nordstrom",
  kohls: "Kohl's",
  wayfair: "Wayfair",
  ikea: "IKEA",
};

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
}

/** Well-known retailer display name for a URL, or null when unrecognized. */
export function getRetailerName(url: string): string | null {
  const host = hostOf(url);
  if (!host) return null;
  const known = RETAILERS[hostParts(host).name];
  if (known) return known;
  return lookupOfferMerchant(host)?.name ?? null;
}

/** "home-appliances" → "Home Appliances", "omnihearing" → "Omnihearing". */
function titleCase(label: string): string {
  return label
    .split(/[-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}



/**
 * The store's own name from its domain when the retailer isn't a known one:
 * the site's name, never a subdomain ("shop.omnihearing.ca" → "Omnihearing",
 * "somestore.co.uk" → "Somestore"). With the product, the maker's own site is
 * named by its brand ("starkeycanada.ca" → "Starkey").
 */
export function getDisplayDomain(
  url: string,
  product?: { title?: string | null; brandName?: string | null } | null,
): string {
  const host = hostOf(url);
  if (!host) return "Store";
  // The maker's own site is named by its brand, only when the site's
  // registrable name is the brand (makerBrandOf: never a lookalike).
  const brand = makerBrandOf(url, product);
  if (brand) return brand;
  const { subdomains, name, suffix } = hostParts(host);
  // A brand's own top-level domain (home-appliances.philips): the brand is the TLD.
  if (suffix.length === 1 && suffix[0].length >= 4 && BRAND_TLDS.has(suffix[0])) return titleCase(suffix[0]);
  // A pseudo-suffix (theordinary.uk.com): the name is the label before the country code.
  const label = name.length === 2 && suffix.length === 1 && subdomains.length > 0 ? subdomains[subdomains.length - 1] : name;
  // A brand's regional site ("starkeycanada.ca"): its own name in words,
  // "Starkey Canada". A name, never the "Official site" claim (makerBrandOf).
  const brandWord = (product?.brandName ?? "").trim() || (product?.title ?? "").trim().split(/\s+/)[0] || "";
  const brandKey = brandWord.toLowerCase().replace(/[^a-z0-9]+/g, "");
  const country = brandKey.length >= 3 && label.startsWith(brandKey) ? label.slice(brandKey.length).replace(/^-+/, "") : "";
  if (country && MARKET_WORDS.has(country)) return `${brandWord} ${titleCase(country)}`;
  return titleCase(label) || "Store";
}

/** Market names a brand's regional site adds to its own ("starkeycanada"). */
const MARKET_WORDS = new Set(["canada", "usa", "us", "uk", "australia", "france", "deutschland", "germany", "espana", "italia", "mexico", "europe"]);

/** Best available store label: known retailer name, else the brand or the site's name. */
export function getRetailerDisplayName(
  url: string,
  product?: { title?: string | null; brandName?: string | null } | null,
): string {
  return getRetailerName(url) ?? getDisplayDomain(url, product);
}
